import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Fingerprint, ChevronLeft } from "lucide-react";
import "bootstrap/dist/css/bootstrap.min.css";

export default function Unauthorized() {
  const navigate = useNavigate();

  return (
    <div className="security-screen d-flex flex-column justify-content-between vh-100 overflow-hidden text-white position-relative">
      
      {/* 1. Background Gradient Mesh */}
      <div className="bg-glow"></div>

      {/* 2. Top Bar (System Status) */}
      <div className="d-flex justify-content-between align-items-center p-4" style={{zIndex: 2, marginTop:"70px"}}>
        <div className="d-flex align-items-center gap-2">
            <span className="status-dot"></span>
            <span className="small text-white-50 fw-bold letter-spacing-1">SYSTEM LOCKED</span>
        </div>
        <span className="small text-white-50 font-monospace">ERR_403</span>
      </div>

      {/* 3. Center Content (The Scanner) */}
      <div className="flex-grow-1 d-flex flex-column justify-content-center align-items-center position-relative" style={{zIndex: 2}}>
        
        {/* Scanner Container */}
        <div className="scanner-wrapper mb-5">
            <motion.div 
                className="lock-icon"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
            >
                <Lock size={64} strokeWidth={1.5} className="text-white" />
            </motion.div>
            
            {/* Laser Line Animation */}
            <motion.div 
                className="scanner-line"
                animate={{ top: ["0%", "100%", "0%"] }}
                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            />
            <div className="scanner-grid"></div>
        </div>

        <motion.h1 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="fw-bold display-5 mb-2 text-center"
        >
            Access Denied
        </motion.h1>

        <motion.p 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-white-50 text-center px-4" 
            style={{maxWidth: "300px", fontSize: "0.95rem"}}
        >
            Your Role does not have the required clearance level for this area.
        </motion.p>
      </div>

      {/* 4. Bottom Action (Thumb Friendly) */}
      <motion.div 
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="p-4 w-100" 
        style={{zIndex: 2}}
      >
        <button 
            onClick={() => navigate('/')}
            className="btn btn-action w-100 py-3 rounded-4 d-flex align-items-center justify-content-center gap-2 shadow-lg"
        >
            <ChevronLeft size={20} />
            <span className="fw-bold">Return to Safety</span>
        </button>
        
        <div className="text-center mt-3">
            <Fingerprint size={24} className="text-white-25" />
        </div>
      </motion.div>

      {/* CSS Styles */}
      <style>{`
        /* الخلفية الفخمة */
        .security-screen {
            background-color: #050505; /* Blackest Black */
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        }

        /* الإضاءة الخلفية */
        .bg-glow {
            position: absolute;
            width: 100%;
            height: 60%;
            bottom: 0;
            background: radial-gradient(circle at center, rgba(64, 224, 208, 0.15) 0%, transparent 70%);
            z-index: 1;
        }

        /* نقطة الحالة */
        .status-dot {
            width: 15px;
            height: 15px;
            background-color: #ff3b30; /* System Red */
            border-radius: 50%;
            box-shadow: 0 0 10px #ff3b30;
            animation: blink 2s infinite;
        }

        .letter-spacing-1 { letter-spacing: 2px; }

        /* منطقة الماسح الضوئي */
        .scanner-wrapper {
            position: relative;
            width: 120px;
            height: 120px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 24px;
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            overflow: hidden;
            box-shadow: 0 0 30px rgba(0,0,0,0.5);
        }

        /* خط الليزر */
        .scanner-line {
            position: absolute;
            left: 0;
            width: 100%;
            height: 2px;
            background: #40e0d0; /* Turquoise */
            box-shadow: 0 0 10px #40e0d0;
            z-index: 5;
        }

        /* شبكة الماسح */
        .scanner-grid {
            position: absolute;
            inset: 0;
            background-image: linear-gradient(rgba(64, 224, 208, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(64, 224, 208, 0.1) 1px, transparent 1px);
            background-size: 20px 20px;
            opacity: 0.5;
        }

        /* الزرار الرئيسي */
        .btn-action {
            background: white;
            color: black;
            border: none;
            transition: transform 0.2s;
        }
        .btn-action:active {
            transform: scale(0.96);
        }

        @keyframes blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
}
